import styled, { css } from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { tabletBreakpoint, desktopBreakpoint, mobileBreakpoint } from 'react-components'
import ChevronRight from '../../../assets/icons/chevron-right.svg'

export const Container = styled.section(
    ({ theme }) => css`
        position: relative;
        overflow: visible;
        margin: ${theme.spacing.medium} 0;
        padding: 0 ${theme.spacing.large} 0;

        @media only screen and (max-width: ${desktopBreakpoint.max}px) {
            padding: 0 ${theme.spacing.large};
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            padding: 0 ${theme.spacing.medium} 0;
        }
    `,
)

export const TitleSubtitleWrapper = styled.div(
    ({ theme }) => css`
        margin-bottom: ${theme.spacing.medium};
        padding: ${theme.spacing.small} ${theme.spacing.xxSmall};

        @media only screen and (max-width: ${tabletBreakpoint.max}px) {
            margin-bottom: 1.25rem;
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            padding: ${theme.spacing.small} 0 ${theme.spacing.xxSmall};
            margin-bottom: ${theme.spacing.medium};
        }
    `,
)

export const TitleContainer = styled.h2(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 600;
        font-size: 1.5625rem;
        line-height: 1.875rem;
        color: ${theme.colors.tfwBlack99};

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            font-size: ${theme.typography.font.size.large};
            line-height: 1.75rem;
        }
    `,
)

export const SubtitleContainer = styled.p(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: ${theme.typography.font.size.medium};
        font-weight: 400;
        line-height: 1.5rem;
        color: #5f5f5f;
        margin-top: ${theme.spacing.xxSmall};

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            font-size: 0.875rem;
            line-height: 1.375rem;
            margin-top: ${theme.spacing.xxxSmall};
        }
    `,
)

export const ContentWrapper = styled.section(
    ({ theme }) => css`
        display: flex;
        margin: 0 auto ${theme.spacing.medium};
        width: 100%;
        gap: ${theme.spacing.xxLarge};
        align-items: flex-start;

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            flex-direction: column;
            gap: ${theme.spacing.medium};
        }
    `,
)

export const SidebarContainer = styled.div`
    max-width: 17.5rem;
    border: 0.0625rem solid #d3d3d3;
    border-radius: 0.75rem;
    align-self: flex-start;
    position: sticky;
    top: 6.875rem;
    max-height: calc(100vh - 6.875rem);
    overflow-y: auto;
    scroll-behavior: smooth;

    /* Hide scrollbar for Chrome, Safari, Edge */
    &::-webkit-scrollbar {
        display: none;
    }

    /* Hide scrollbar for Firefox */
    scrollbar-width: none;

    /* Hide scrollbar for IE/Edge */
    -ms-overflow-style: none;

    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        position: static;
        top: auto;
        max-height: none;
        max-width: unset;
        width: 100%;
    }
`

export const SectionLinksList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`

interface HeadingItemProps {
    active?: boolean
}

export const SectionLinkContainer = styled.li<HeadingItemProps>(
    ({ theme, active }) => css`
        position: relative;
        cursor: pointer;
        display: flex;
        margin: 0.0625rem ${theme.spacing.xSmall} 0;
        padding: ${theme.spacing.small};
        background: ${active ? '#F5F5F5' : 'transparent'};
        border-radius: 0.75rem;
        transition: all 0.3s ease;

        &:first-child {
            margin: ${theme.spacing.xSmall} ${theme.spacing.xSmall} 0;
        }

        &:last-child {
            margin: 0.0625rem ${theme.spacing.xSmall} ${theme.spacing.xSmall};
        }

        &::after {
            content: '';
            position: absolute;
            left: 10%;
            right: 10%;
            bottom: 0;
            border-bottom: 0.0625rem dotted #f5f5f5;
            pointer-events: none;
        }

        &:last-child::after {
            display: none;
        }
    `,
)

export const SectionLink = styled.a(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        text-decoration: none;
        color: inherit;
        width: 100%;
        gap: ${theme.spacing.xSmall};
        justify-content: space-between;
    `,
)

export const SectionLinkTitle = styled.p(
    ({ theme }) => css`
        flex: 1;
        font-family: ${theme.typography.font.family.latoVerdana};
        font-size: ${theme.typography.font.size.medium};
        font-weight: 400;
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const ArrowContainer = styled.div<{ active?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    width: 1.5rem;
    border-radius: 50%;
    background: ${({ active }) => (active ? '#fff' : '#f5f5f5')};
    transition: background 0.3s ease;
`

export const ArrowRightIcon = styled(ChevronRight)`
    height: 100%;
    width: 100%;

    & path {
        stroke: #a5a5a5;
    }

    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        transform: rotate(90deg);
        transform-origin: center; /* ensures rotation around center */
    }
`

export const ContentContainer = styled.div`
    flex: 1;
    min-width: 0;
`

export const ContentSection = styled.div(
    ({ theme }) => css`
        margin-bottom: ${theme.spacing.xxLarge};
        scroll-margin-top: 6.875rem;

        /* creates a new block formatting context so the float clears */
        display: flow-root;

        &:last-child {
            margin-bottom: 0;
        }
    `,
)

export const HeadingImageWrapper = styled.div`
    display: block;

    @media only screen and (max-width: ${tabletBreakpoint.max}px) {
        display: flex;
        flex-direction: column-reverse;
    }

    @media only screen and (max-width: ${mobileBreakpoint.max}px) {
        display: flex;
        flex-direction: column;
    }
`

export const SectionHeading = styled.h3(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.latoVerdana};
        font-weight: 600;
        font-size: ${theme.typography.font.size.large};
        line-height: 1.75rem;
        margin-bottom: ${theme.spacing.xxSmall};
        color: ${theme.colors.tfwBlack99};

        @media only screen and (max-width: ${tabletBreakpoint.max}px) {
            margin-bottom: ${theme.spacing.small};
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            margin-bottom: ${theme.spacing.xSmall};
        }
    `,
)

export const ImageWrapper = styled.div(
    ({ theme }) => css`
        float: right;
        width: 20.5rem;
        aspect-ratio: 20.5 / 13.625;
        margin: ${theme.spacing.xxLarge} 0 ${theme.spacing.large} ${theme.spacing.large};
        border-radius: 1rem;
        background-color: #f0f0f0;
        overflow: hidden;

        @media only screen and (max-width: ${tabletBreakpoint.max}px) {
            float: none;
            margin: ${theme.spacing.large} auto;
            width: 100%;
            max-width: 18.75rem;
        }

        @media only screen and (max-width: ${mobileBreakpoint.max}px) {
            margin: 0 auto ${theme.spacing.xSmall};
            width: 100%;
            max-width: none;
        }
    `,
)

export const SectionImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`

export const CustomMarkdown = styled(ReactMarkdown)(
    ({ theme }) => css`
        & ul,
        li {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.latoVerdana};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.small} ${theme.spacing.medium};
            padding: 0;
        }

        & p,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
            font: ${theme.typography.font.lato.weight.regular} ${theme.typography.font.size.medium} / 1.5rem
                ${theme.typography.font.family.latoVerdana};
            color: ${theme.colors.tfwBlack99};
            margin: ${theme.spacing.medium} 0;
            padding: 0;
        }

        & strong {
            color: #4a4a4a;
        }

        & p:first-child {
            margin-top: 0;
        }

        & p:last-child {
            margin-bottom: 0;
        }

        & a {
            color: #016bd4;
            text-decoration: none;

            &:hover {
                color: #0159b8;
                text-decoration: underline;
            }
        }
    `,
)
